<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card-text>
      <v-card class="mx-auto" elevation="0" title="Demographic baseline numbers"
              ref="demographic-baseline-numbers">
        <v-row no-gutters>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <number-box :number="demographicBaselineTotalNumberOfPatients.value"
                        :label="demographicBaselineTotalNumberOfPatients.label"
                        :comment="demographicBaselineTotalNumberOfPatients.comment"/>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <combined-pie-chart-and-table :chart-data="demographicBaselineChartData"
                                          :chart-comment="demographicBaselineComment"
                                          :table-headers="demographicBaselineNumbersHeaders"
                                          :table-data="demographicBaselineNumbers"
                                          :table-comment="demographicBaselineComment"
                                          :visible="visible"/>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <number-box :number="demographicBaselineTotalTimeSpanInYears.value"
                        :label="demographicBaselineTotalTimeSpanInYears.label"
                        :comment="demographicBaselineTotalTimeSpanInYears.comment"/>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <number-box :number="demographicBaselineTotalNumberOfPrevalentPatients.value"
                        :label="demographicBaselineTotalNumberOfPrevalentPatients.label"
                        :comment="demographicBaselineTotalNumberOfPrevalentPatients.comment"/>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="number-box-left">
                <v-data-table :headers="demographicBaselineCauseOfDeathNumbersHeaders"
                              :items="demographicBaselineCauseOfDeathNumbers"
                              density="compact"
                              :fixed-header="true"
                              height="200px"
                              :items-per-page="0">
                  <template #bottom></template>
                  <template v-slot:[`item.percentage`]="{ item }">
                    <span style="color:#b2df8a">{{ item.percentage }}%</span>
                  </template>
                  <template v-slot:no-data>
                    <v-progress-circular color="primary" v-if="loading" indeterminate></v-progress-circular>
                  </template>
                </v-data-table>
                <div class="label large-number-comment">{{ demographicBaselineCauseOfDeathComment }}</div>
              </div>
            </v-sheet>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col class="text-right">
            <vue-to-print :content="() => this.$refs['demographic-baseline-numbers'].$el"
                          :document-title="icd + '-' + 'demographic-baseline-numbers'"
                          remove-after-print>
              <template v-slot:trigger>
                <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
              </template>
            </vue-to-print>
          </v-col>
        </v-row>
      </v-card>
      <v-row no-gutters>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0" title="Number of new cases"
                  ref="number-of-diagnosed-patients">
            <v-card-subtitle class="text-wrap">{{ totalNumberOfPatientsPerYearChartSubtitle }}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <GChart type="BarChart" :data="totalNumberOfPatientsPerYearChartData"
                        :options="totalNumberOfPatientsPerYearChartOptions"
                        :key="'totalNumberOfPatientsPerYearChart' + totalNumberOfPatientsPerYearChartKeyIndex"
                        v-if="totalNumberOfPatientsPerYearChartData.length !== 0"/>
                <v-progress-circular color="primary" indeterminate v-else></v-progress-circular>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['number-of-diagnosed-patients'].$el"
                              :document-title="icd + '-' + 'number-of-diagnosed-patients'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0" title="Number of deaths"
                  ref="number-of-deceased-patients">
            <v-row no-gutters>
              <v-col>
                <GChart type="BarChart" :data="totalNumberOfDeceasedPatientsPerYearChartData"
                        :options="totalNumberOfDeceasedPatientsPerYearChartOptions"
                        :key="'totalNumberOfDeceasedPatientsPerYearChart' + totalNumberOfDeceasedPatientsPerYearChartKeyIndex"
                        v-if="totalNumberOfDeceasedPatientsPerYearChartData.length !== 0"/>
                <v-progress-circular color="primary" indeterminate v-else></v-progress-circular>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['number-of-deceased-patients'].$el"
                              :document-title="icd + '-' + 'number-of-deceased-patients'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";
import { VueToPrint } from "vue-to-print";
import CombinedPieChartAndTable from "@/components/CombinedPieChartAndTable.vue";
import NumberBox from "@/components/NumberBox.vue";

export default defineComponent({
  name: 'RareDiseaseDemographicBaseline',
  props: {
    icd: String,
    icdDescription: String,
    visible: Boolean
  },
  components: {
    NumberBox,
    GChart,
    CombinedPieChartAndTable,
    VueToPrint
  },
  data: () => ({
    loading: false,
    demographicBaselineTotalNumberOfPatients: {},
    demographicBaselineTotalTimeSpanInYears: {},
    demographicBaselineNumbersHeaders: [
      {
        title: 'Patient population',
        align: 'start',
        key: 'label',
      },
      {
        title: 'Value',
        align: 'end',
        key: 'value',
      },
      {
        title: 'Percentage',
        align: 'end',
        key: 'percentage',
      }],
    demographicBaselineNumbers: [],
    demographicBaselineComment: '',
    selectedDemographicBaseline: 'demographicBaselinePieChartValue',
    demographicBaselineChartKeyIndex: 0,
    demographicBaselineChartData: [],
    demographicBaselineChartOptions: {
      title: '',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 250,
      colors: ['#a6cee3', '#06a0f3', '#b2df8a', '#67dd00', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: {
        position: 'top',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      tooltip: {
        isHtml: true
      }
    },
    demographicBaselineTotalNumberOfPrevalentPatients: {},
    demographicBaselineCauseOfDeathNumbersHeaders: [
      {
        title: 'Patient population',
        align: 'start',
        key: 'label',
      },
      {
        title: 'Value',
        align: 'end',
        key: 'value',
      },
      {
        title: 'Percentage',
        align: 'end',
        key: 'percentage',
      }],
    demographicBaselineCauseOfDeathNumbers: [],
    demographicBaselineCauseOfDeathComment: '',
    totalNumberOfPatientsPerYearChartKeyIndex: 0,
    totalNumberOfPatientsPerYearChartSubtitle: '',
    totalNumberOfPatientsPerYearChartData: [],
    totalNumberOfPatientsPerYearChartOptions: {
      title: '',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#b2df8a'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: 'none',
      hAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      annotations: {
        textStyle: {
          fontSize: 12,
          color: '#808080',
          auraColor: 'none'
        }
      },
      tooltip: {
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    totalNumberOfDeceasedPatientsPerYearChartKeyIndex: 0,
    totalNumberOfDeceasedPatientsPerYearChartData: [],
    totalNumberOfDeceasedPatientsPerYearChartOptions: {
      title: '',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#fdbf6f'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: 'none',
      hAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      annotations: {
        textStyle: {
          fontSize: 12,
          color: '#808080',
          auraColor: 'none'
        }
      },
      tooltip: {
        isHtml: true
      },
      intervals: {
        'style': 'boxes',
        'boxWidth': 1,
        'lineWidth': 0,
        'fillOpacity': 0.2
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    }
  }),
  methods: {
    forceRerender() {
      this.demographicBaselineChartKeyIndex += 1;
      this.totalNumberOfPatientsPerYearChartKeyIndex += 1;
      this.totalNumberOfDeceasedPatientsPerYearChartKeyIndex += 1;
    },
    async loadDemographicBaselineTotalNumberOfPatientsData() {
      let data = axios.get('rare-disease/demographic-baseline-total-number-of-patients',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.demographicBaselineTotalNumberOfPatients = response.data;
      });
    },
    async loadDemographicBaselineTotalTimeSpanInYearsData() {
      let data = axios.get('rare-disease/demographic-baseline-total-time-span-in-years',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.demographicBaselineTotalTimeSpanInYears = response.data;
      });
    },
    async loadDemographicBaselineData() {
      let data = axios.get('rare-disease/demographic-baseline',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.demographicBaselineNumbers = response.data.data;
        this.demographicBaselineComment = response.data.comment;
      });
    },
    async loadDemographicBaselineChartData() {
      let data = axios.get('/rare-disease/demographic-baseline-chart',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.demographicBaselineChartData = response.data;
      });
    },
    async loadDemographicBaselineTotalNumberOfPrevalentPatientsData() {
      let data = axios.get('rare-disease/demographic-baseline-total-number-of-prevalent-patients',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.demographicBaselineTotalNumberOfPrevalentPatients = response.data;
      });
    },
    async loadDemographicBaselineCauseOfDeathData() {
      let data = axios.get('rare-disease/demographic-baseline-cause-of-death',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              icd_description: this.icdDescription
            }
          });
      return data.then((response) => {
        this.demographicBaselineCauseOfDeathNumbers = response.data.data;
        this.demographicBaselineCauseOfDeathComment = response.data.comment;
      });
    },
    async loadTotalNumberOfPatientsPerYearChartData() {
      let data = axios.get('rare-disease/total-number-per-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.totalNumberOfPatientsPerYearChartData = response.data.data;
        this.totalNumberOfPatientsPerYearChartOptions.title = response.data.title;
        this.totalNumberOfPatientsPerYearChartSubtitle = response.data.comment;
      });
    },
    async loadTotalNumberOfDeceasedPatientsPerYearChartData() {
      let data = axios.get('rare-disease/total-number-deceased-per-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.totalNumberOfDeceasedPatientsPerYearChartData = response.data.data;
        this.totalNumberOfDeceasedPatientsPerYearChartOptions.title = response.data.comment;
      });
    }
  },
  async mounted() {
    this.loading = true;
    await Promise.all([
      this.loadDemographicBaselineTotalNumberOfPatientsData(),
      this.loadDemographicBaselineTotalTimeSpanInYearsData(),
      this.loadDemographicBaselineData(),
      this.loadDemographicBaselineChartData(),
      this.loadDemographicBaselineTotalNumberOfPrevalentPatientsData(),
      this.loadDemographicBaselineCauseOfDeathData(),
      this.loadTotalNumberOfPatientsPerYearChartData(),
      this.loadTotalNumberOfDeceasedPatientsPerYearChartData()
    ]).finally(() => {
      this.loading = false;
    });
  },
  updated() {
    if (this.visible) {
      this.forceRerender()
    }
  }
});
</script>

<style scoped>
.number-box-left {
  height: 375px;
  justify-content: space-around;
  text-align: left;
  padding: 20px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.large-number-comment {
  font-size: 0.8em;
  margin-top: 10px;
  margin-left: 20px;
  margin-right: 20px;
}

@media print {
  .mx-auto {
    zoom: 50%;
  }
}
</style>
